import React from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Flex from "components/common/Flex";
import { useSelector } from "react-redux";

ChartJS.register(ArcElement, Tooltip, Legend);

// Plugin to draw center text
const centerTextPlugin = {
  id: "centerText",
  beforeDraw: (chart) => {
    const { ctx, width, height } = chart;
    ctx.save();

    const fontSize = Math.min(width, height) * 0.08;
    ctx.font = `${fontSize}px Arial`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillStyle = "#6c757d";

    ctx.fillText("Market Share", width / 2, height / 2);
    ctx.restore();
  },
};

// Color palette for payors - matching main dashboard theme
const colorPalette = [
  "#2c7ce6", // Blue
  "#0da9bd", // Teal
  "#f5803e", // Orange
  "#ed5e0b", // Red-orange
  "#26bcfc", // Cyan
  "#d16e34", // Brown-orange
  "#2a91ed", // Light blue
  "#269dd0", // Blue
  "#197aa4", // Dark blue
  "#b8571f", // Dark orange
  "#a9e2fd", // Light blue
  "#6c757d", // Gray
  "#343a40", // Dark gray
];

const ClaimStatusItem = ({ item }) => {
  return (
    <Flex
      alignItems="center"
      justifyContent="between"
      className="fw-semibold fs-11 mb-1"
    >
      <p className="mb-0 d-flex align-items-center text-capitalize">
        <FontAwesomeIcon
          icon="circle"
          className="me-2"
          style={{ color: item.color }}
        />
        {item.label}
      </p>
      <div>{`${item.totalAmount.toLocaleString()} (${item.percentage})`}</div>
    </Flex>
  );
};

const InsuranceMarketShareChart = () => {
  const { claimDashboardData } = useSelector((state) => state.claimDashboard);
  const claimPayers = claimDashboardData?.claim_payor || [];

  // Calculate total gross amount
  const totalGrossAmount = claimPayers.reduce(
    (sum, payer) => sum + (payer.tot_submtamount || 0),
    0
  );

  // Generate legend items
  const legendItems = claimPayers.map((payer, index) => ({
    label: payer.payor_name,
    percentage: totalGrossAmount
      ? ((payer.tot_submtamount / totalGrossAmount) * 100).toFixed(1) + "%"
      : "0.0%",
    totalAmount: payer.tot_submtamount || 0,
    color: colorPalette[index % colorPalette.length],
  }));

  // Chart data
  const data = {
    labels: legendItems.map((item) => item.label),
    datasets: [
      {
        data: legendItems.map((item) =>
          parseFloat(item.percentage.replace("%", ""))
        ),
        backgroundColor: legendItems.map((item) => item.color),
        borderWidth: 2,
        borderColor: "#ffffff",
        hoverBorderWidth: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0,0,0,0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        callbacks: {
          label: (context) => `${context.label}: ${context.parsed}%`,
        },
      },
    },
    interaction: { intersect: false },
  };

  return (
    <Col xs={12} md={6}>
      <div className="card h-100 shadow-sm">
        <div className="card-body">
          <h5 className="fs-9 text-primary fw-normal">Insurance Market Share</h5>

          <div
            className="d-flex align-items-center"
            style={{ height: "220px" }}
          >
            {/* Legend */}
            <Col xs={12} md={7}>
              <Row>
                <Col xs={6}>
                  {legendItems
                    .slice(0, Math.ceil(legendItems.length / 2))
                    .map((item, i) => (
                      <ClaimStatusItem key={i} item={item} />
                    ))}
                </Col>
                <Col xs={6}>
                  {legendItems
                    .slice(Math.ceil(legendItems.length / 2))
                    .map((item, i) => (
                      <ClaimStatusItem
                        key={i + Math.ceil(legendItems.length / 2)}
                        item={item}
                      />
                    ))}
                </Col>
              </Row>
            </Col>

            {/* Pie Chart */}
            <div className="flex-grow-1 position-relative">
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "60%",
                  transform: "translate(-50%, -50%)",
                  width: "160px",
                  height: "160px",
                }}
              >
                <Pie
                  data={data}
                  options={options}
                  plugins={[centerTextPlugin]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Col>
  );
};

export default InsuranceMarketShareChart;